import { useInView } from "../../../hooks/useInView";
import { events } from "../../../mocks/events";

export default function PastEvents() {
  const past = events.filter((e) => e.status === "past");
  const { ref, inView } = useInView();

  return (
    <>
      {/* Full-width dark divider banner */}
      <div className="w-full bg-zinc-900 py-8 px-6 lg:px-10 relative overflow-hidden">
        <div className="absolute inset-0 bg-dot-grid-dark pointer-events-none" />
        <div className="relative max-w-7xl mx-auto flex items-center gap-6">
          <div className="h-px flex-1 bg-gradient-to-r from-transparent to-zinc-700" />
          <div className="flex items-center gap-3 flex-shrink-0">
            <div className="w-6 h-6 flex items-center justify-center">
              <i className="ri-archive-fill text-orange-500" />
            </div>
            <span className="font-condensed font-black text-white text-xs uppercase tracking-[0.25em]">
              Event Archives
            </span>
          </div>
          <div className="h-px flex-1 bg-gradient-to-l from-transparent to-zinc-700" />
        </div>
      </div>

      <section
        ref={ref as React.RefObject<HTMLElement>}
        className="bg-gray-50 py-16 md:py-24 relative overflow-hidden"
      >
        {/* Subtle dot grid */}
        <div className="absolute inset-0 bg-dot-grid-light pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <div className={`mb-12 anim-hidden ${inView ? "animate-fade-in-up" : ""}`}>
            <div className="inline-block border border-gray-300 text-gray-500 font-condensed font-semibold text-xs uppercase tracking-widest px-3 py-1 mb-4">
              Event Archives
            </div>
            <h2 className="font-condensed font-black text-gray-900 text-4xl md:text-5xl uppercase tracking-tight">
              Past Events
            </h2>
            <div className={`mt-3 h-0.5 bg-orange-500 w-16 anim-hidden ${inView ? "line-grow delay-200" : ""}`} />
            <p className="text-gray-500 text-sm mt-3 max-w-xl">
              Relive the action from PWA&apos;s most memorable nights. Every event, every result, preserved in the archives.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {past.map((event, i) => (
              <div
                key={event.id}
                className={`bg-white border border-gray-200 overflow-hidden group hover:border-orange-300 hover:-translate-y-1 transition-all duration-300 anim-hidden ${inView ? "animate-fade-in-up" : ""}`}
                style={{ animationDelay: `${150 + i * 120}ms` }}
              >
                {/* Poster */}
                <div className="w-full h-52 overflow-hidden bg-gray-100 relative">
                  <img
                    src={event.image}
                    alt={event.title}
                    className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                  />
                  {/* Brand-tinted overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-zinc-900/75 via-orange-950/25 to-orange-900/15" />
                  <div className="absolute bottom-3 left-3">
                    <span className="bg-orange-500 text-white font-condensed font-bold text-xs uppercase tracking-widest px-2 py-1">
                      {event.date}
                    </span>
                  </div>
                  {/* Hover arrow */}
                  <div className="absolute top-3 right-3 w-8 h-8 bg-orange-500 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 translate-x-2 group-hover:translate-x-0">
                    <i className="ri-arrow-right-up-line text-white text-sm" />
                  </div>
                </div>

                {/* Content */}
                <div className="p-5">
                  <p className="text-gray-400 font-condensed font-semibold text-xs uppercase tracking-widest mb-1">
                    {event.venue} — {event.city}
                  </p>
                  <h3 className="font-condensed font-black text-gray-900 text-xl uppercase tracking-tight mb-3 group-hover:text-orange-500 transition-colors duration-200">
                    {event.title}
                  </h3>
                  <p className="text-gray-500 text-xs leading-relaxed mb-4 line-clamp-3">
                    {event.description}
                  </p>

                  <div className="flex flex-wrap gap-1.5">
                    {event.highlights.slice(0, 3).map((h, j) => (
                      <span
                        key={j}
                        className="border border-gray-200 text-gray-500 font-condensed font-semibold text-xs uppercase tracking-wide px-2 py-0.5 hover:border-orange-300 transition-colors duration-200"
                      >
                        {h}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
